import { Component } from '@theme/component';

/**
 * Smoothly scrolls the page so the top of a target section lands just below the
 * sticky header (i.e. the section top becomes the top of the visible page).
 *
 * Usage:
 *   <scroll-to-section data-target=".watch-split">
 *     <button on:click="/scrollToTarget">View in 360°</button>
 *   </scroll-to-section>
 *
 * If no element matches `data-target` on the current page, the component hides
 * itself so the link never scrolls to nothing.
 *
 * @extends Component
 */
class ScrollToSection extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Hide the trigger entirely when the destination isn't on this page.
    if (!this.#target) this.hidden = true;
  }

  /**
   * The destination element, resolved fresh each time in case the DOM changed.
   * @returns {HTMLElement | null}
   */
  get #target() {
    const selector = this.getAttribute('data-target');
    return selector ? document.querySelector(selector) : null;
  }

  /**
   * @param {Event} [event]
   */
  scrollToTarget = (event) => {
    event?.preventDefault();

    const target = this.#target;
    if (!target) return;

    const headerHeight =
      parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - headerHeight;

    window.scrollTo({ top, behavior: 'smooth' });
  };
}

if (!customElements.get('scroll-to-section')) {
  customElements.define('scroll-to-section', ScrollToSection);
}
